import { config } from "../../config";
import { store } from "../store";

/**
 * Web port of the RN `cloudinaryUpload` middleware. Accepts a data URL
 * (from a file input) or a raw base64 string and uploads it to Cloudinary
 * through the backend `/api/sponsor/uploadImage` proxy.
 *
 * The endpoint is access-token gated, so calls made before login (e.g. during
 * signup) fail silently and return `undefined` — callers treat that as "skip".
 */
export const cloudinaryUpload = async (image: string) => {
  if (!image) return undefined;

  const dataUri = image.startsWith("data:")
    ? image
    : `data:image/jpeg;base64,${image}`;
  const appKey = store.getState().auth.app_key;
  const accessToken = store.getState().auth.access_token;

  try {
    const response = await fetch(`${config.API_URL}/api/sponsor/uploadImage`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: config.API_KEY,
        appkey: appKey || config.APP_KEY,
        accesstoken: accessToken || "",
      },
      body: JSON.stringify({ imageBase64: dataUri, folder: "uninfohk/sponsor" }),
    });
    const result = await response.json();
    if (result.status === 1 && result.resp) {
      return result.resp;
    }
    return undefined;
  } catch (err) {
    console.error(`cloudinaryUpload ${err}`);
    return undefined;
  }
};

/**
 * Upload a list of images which may mix freshly picked `data:` URLs with
 * already-hosted Cloudinary URLs (kept as-is). Used by the event create /
 * edit services, where the wizard holds both kinds side by side.
 */
export const cloudinaryUploadList = async (images: string[]): Promise<string[]> => {
  const uploaded: string[] = [];
  for (const image of images) {
    if (!image) continue;
    uploaded.push(
      image.startsWith("data:") ? (await cloudinaryUpload(image)) ?? "" : image,
    );
  }
  return uploaded;
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Web port of the RN `cloudinaryUploadMedia` middleware: uploads a batch of
 * picked media (images and/or videos) to the same `/api/sponsor/uploadImage`
 * proxy, which detects the Cloudinary resource type from the data URI MIME
 * type. Already-hosted URLs are passed through unchanged. Files that fail to
 * upload are dropped, so the returned array may be shorter than the input.
 */
export const cloudinaryUploadMedia = async (
  files: (File | string)[],
  folder = "uninfohk/sponsor",
): Promise<string[]> => {
  const appKey = store.getState().auth.app_key;
  const accessToken = store.getState().auth.access_token;

  const upload = async (dataUri: string) => {
    const response = await fetch(`${config.API_URL}/api/sponsor/uploadImage`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: config.API_KEY,
        appkey: appKey || config.APP_KEY,
        accesstoken: accessToken || "",
      },
      body: JSON.stringify({ imageBase64: dataUri, folder }),
    });
    const result = await response.json();
    return result.status === 1 && result.resp ? (result.resp as string) : undefined;
  };

  const uploaded = await Promise.all(
    files.map(async (file) => {
      try {
        if (typeof file === "string") {
          return file.startsWith("data:") ? await upload(file) : file;
        }
        return await upload(await readFileAsDataUrl(file));
      } catch (err) {
        console.error(`cloudinaryUploadMedia ${err}`);
        return undefined;
      }
    }),
  );
  return uploaded.filter((url): url is string => Boolean(url));
};
